/* eslint-disable */
import React, { memo, useState, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	FaAddressBook,
	FaArrowLeft,
	FaBuilding,
	FaClipboardList,
	FaHome,
	FaTools,
	FaUsers,
} from 'react-icons/fa';

import { Link, useLocation } from 'react-router-dom';
import { APP_TOKEN } from '../../helpers/Constants';
import { getSubscriptionSummary } from '../../pages/Profile/Store';
import { GET, POST } from '../../service/HttpMethod';
import { RootState } from '../../store/reducers';
import {
	Header,
	HeaderLogo,
	HeaderLeft,
	HamburgerMenu,
	HeaderRight,
	PageWrapper,
	InnerWrapper,
	PageContainer,
	Avatar,
	Sidebar,
	SidebarList,
	SidebarListitem,
	Dropdown,
	DropdownList,
} from '../../style';
import { hasProjectFeatureSelection } from '../../pages/ProjectDetails/projectFeatureNavigation';
import { PRIVACY_ROUTE, TERMS_ROUTE } from '../../legal/legalDocuments';
import NewFeatureTutorial from './NewFeatureTutorial';
import './AppLayout.css';

interface PrimaryNavLinkProps {
	active: boolean;
	description: string;
	icon: ReactNode;
	label: string;
	labelLines?: string[];
	to: string;
}

const PrimaryNavLink = ({
	active,
	description,
	icon,
	label,
	labelLines,
	to,
}: PrimaryNavLinkProps) => (
	<Link aria-current={active ? 'page' : undefined} className="app-shell-nav__link" to={to}>
		<SidebarListitem active={active} className="app-shell-nav__item" data-active={active}>
			<span className="app-shell-nav__icon" aria-hidden="true">
				{icon}
			</span>
			<span className="app-shell-nav__copy">
				<strong>
					{labelLines ? labelLines.map((line) => <span key={line}>{line}</span>) : label}
				</strong>
				<small>{description}</small>
			</span>
		</SidebarListitem>
	</Link>
);

const projectBackLinkStyle: React.CSSProperties = {
	alignItems: 'center',
	background: '#FFFFFF',
	border: '1px solid #CBD8E3',
	borderRadius: '6px',
	color: '#315F83',
	display: 'inline-flex',
	fontWeight: 700,
	gap: '8px',
	marginBottom: '14px',
	padding: '9px 13px',
	textDecoration: 'none',
};

const REVIEW_ROUTES_WITH_COLLAPSED_SIDEBAR = ['/result', '/changeAnalysis', '/newDetailsResult'];

interface TradesNavigationAccess {
	subscriptionSummary: any;
	summaryFetching: boolean;
	tradeCreation: string | null;
}

const normalizedBoolean = (value: any): boolean | undefined => {
	if (value === true || value === false) return value;
	if (value === 1 || value === '1') return true;
	if (value === 0 || value === '0') return false;
	if (typeof value !== 'string') return undefined;

	const normalized = value.trim().toLowerCase();
	if (normalized === 'true') return true;
	if (normalized === 'false') return false;
	return undefined;
};

export const shouldShowTradesNavigation = ({
	subscriptionSummary,
	summaryFetching,
	tradeCreation,
}: TradesNavigationAccess): boolean => {
	if (tradeCreation !== 'true' || summaryFetching) return false;

	const hasActiveEnterprisePlan =
		normalizedBoolean(subscriptionSummary?.enterprisePlanData?.isActive) === true;
	if (hasActiveEnterprisePlan) return true;

	return normalizedBoolean(subscriptionSummary?.plan?.isDefault) === false;
};

function AppLayout(props: any) {
	const { children, path = '' } = props;
	const isReviewRoute = REVIEW_ROUTES_WITH_COLLAPSED_SIDEBAR.includes(path);
	const [expand, setExpand] = useState(isReviewRoute);
	const [tutorialOpen, setTutorialOpen] = useState(false);
	const [tutorialRequiresAcknowledgement, setTutorialRequiresAcknowledgement] = useState(false);
	const dispatch = useDispatch();
	const { ss: subscriptionSummary, summaryFetching } = useSelector((state: RootState) => ({
		ss: state.profileStore.ss,
		summaryFetching: state.profileStore.summaryFetching,
	}));
	const userRole = localStorage.getItem('USER_ROLE');
	const isOrganizationUser = userRole === 'OA' || userRole === 'A' || userRole === 'U';
	const [userDisplayName, setUserDisplayName] = useState('');
	const location = useLocation();
	const isProjectFeature =
		path === '/project/:projectId' && hasProjectFeatureSelection(location.search);
	const showTradesNavigation = shouldShowTradesNavigation({
		subscriptionSummary,
		summaryFetching,
		tradeCreation: localStorage.getItem('TRADE_CREATION'),
	});

	useEffect(() => {
		setExpand(isReviewRoute);
	}, [path, isReviewRoute]);

	useEffect(() => {
		if (!localStorage.getItem(APP_TOKEN) || !isOrganizationUser || !summaryFetching) return;
		dispatch(getSubscriptionSummary());
		// The authenticated role and subscription state are established for this layout mount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		window.addEventListener('onbeforeunload', () => {
			if (!window.location.pathname.includes(TERMS_ROUTE)) {
				localStorage.clear();
			}
			if (!window.location.pathname.includes(PRIVACY_ROUTE)) {
				localStorage.clear();
			}
		});
	}, []);

	useEffect(() => {
		if (!localStorage.getItem(APP_TOKEN)) return;
		let active = true;
		GET({ url: 'users/feature-tutorial' })
			.then((response: any) => {
				const payload =
					response?.data?.data !== undefined
						? response.data.data
						: response?.data !== undefined
						? response.data
						: response;
				const displayName = String(
					payload?.displayName ||
						[payload?.firstName, payload?.lastName]
							.map((name) => String(name || '').trim())
							.filter(Boolean)
							.join(' '),
				).trim();
				if (active && displayName) setUserDisplayName(displayName);
				if (active && payload?.show) {
					setTutorialRequiresAcknowledgement(true);
					setTutorialOpen(true);
				}
			})
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, []);

	const projectFeatureBackLink = isProjectFeature ? (
		<Link to={location.pathname} style={projectBackLinkStyle}>
			<FaArrowLeft aria-hidden="true" />
			Back to Project
		</Link>
	) : null;

	return (
		<PageWrapper>
			{path !== '/interactive-review' &&
				path !== '/multi-package-interactive-review/:projectId' &&
				path !== '/project/:projectId/drawings/:splitDocumentId' &&
				path !== '/change-analysis-overlay' &&
				path !== '/change-log-overlay' &&
				path !== '/exclusivity-review/:projectId' && (
					<Header>
						<HeaderLeft>
							<HeaderLogo />
							{!isProjectFeature && (
								<HamburgerMenu
									data-testid="global-sidebar-toggle"
									onClick={() => {
										setExpand(!expand);
									}}
								>
									<span className="icon-hamburger" style={{ color: 'black' }} />
								</HamburgerMenu>
							)}
						</HeaderLeft>
						<HeaderRight className="app-c-dropdown-block">
							<button
								className="feature-tutorial-launch"
								onClick={() => {
									setTutorialRequiresAcknowledgement(false);
									setTutorialOpen(true);
								}}
								type="button"
							>
								Bid Management Tutorial
							</button>
							<Dropdown
								open={false}
								name={
									<div className="app-account-summary">
										{userDisplayName && (
											<span className="app-account-summary__copy">
												<small>Signed in as</small>
												<strong title={userDisplayName}>{userDisplayName}</strong>
											</span>
										)}
										<Avatar>
											<div className="user-info">
												<img
													src={require('../../style/assets/images/avatar-new.png')}
													alt="User Avatar"
												/>
											</div>
										</Avatar>
									</div>
								}
							>
								{localStorage.getItem('USER_ROLE') !== 'PO' && (
									<Link to="/profile">
										<DropdownList>My Profile</DropdownList>
									</Link>
								)}
								<Link to="/changepassword">
									<DropdownList>Change Password</DropdownList>
								</Link>
								<a href={TERMS_ROUTE} target="_blank" rel="noopener noreferrer">
									<DropdownList>Terms & Conditions</DropdownList>
								</a>
								<a href={PRIVACY_ROUTE} target="_blank" rel="noopener noreferrer">
									<DropdownList>Privacy Policy</DropdownList>
								</a>
								<Link to="/signout">
									<DropdownList>Signout</DropdownList>
								</Link>
							</Dropdown>
						</HeaderRight>
					</Header>
				)}
			{tutorialOpen && (
				<NewFeatureTutorial
					onClose={() => setTutorialOpen(false)}
					onDisplayed={
						tutorialRequiresAcknowledgement
							? () => {
									POST({ url: 'users/feature-tutorial/claim' })
										.then(() => setTutorialRequiresAcknowledgement(false))
										.catch(() => undefined);
							  }
							: undefined
					}
				/>
			)}
			{(localStorage.getItem('USER_ROLE') === 'OA' ||
				localStorage.getItem('USER_ROLE') === 'A' ||
				localStorage.getItem('USER_ROLE') === 'U') && (
				<InnerWrapper>
					{path !== '/interactive-review' &&
						path !== '/multi-package-interactive-review/:projectId' &&
						path !== '/project/:projectId/drawings/:splitDocumentId' &&
						path !== '/change-analysis-overlay' &&
						path !== '/groupig-interactive-review' &&
						path !== '/project/:projectId/bidPackageData/:id' &&
						path !== '/change-log-overlay' &&
						path !== '/exclusivity-review/:projectId' &&
						path !== '/groupig-interactive-review' && (
							<Sidebar
								aria-hidden={expand || isProjectFeature}
								className="app-shell-sidebar"
								data-testid="global-sidebar"
								sidebarHide={expand || isProjectFeature}
							>
								{!isProjectFeature && (
									<SidebarList className="app-shell-nav" role="navigation" aria-label="Primary">
										<div className="app-shell-nav__intro">
											<span>Workspace</span>
											<p>Navigate ScrubPlan</p>
										</div>
										<PrimaryNavLink
											active={path === '/home'}
											description="Dashboard overview"
											icon={<FaHome />}
											label="Home"
											to="/home"
										/>
										<PrimaryNavLink
											active={
												path === '/project' ||
												path === '/project/:projectId' ||
												path === '/search' ||
												path === '/duplicates' ||
												path === '/result' ||
												path === '/generateDocument'
											}
											description="Manage active work"
											icon={<FaBuilding />}
											label="Projects"
											to="/project"
										/>
										{showTradesNavigation && (
											<PrimaryNavLink
												active={path === '/trade' || path === '/trade-view/:tradeId'}
												description="Trade templates"
												icon={<FaTools />}
												label="Trades"
												to="/trade"
											/>
										)}
										{(localStorage.getItem('USER_ROLE') === 'OA' ||
											localStorage.getItem('USER_ROLE') === 'A') && (
											<PrimaryNavLink
												active={path === '/organization-inclusions'}
												description="Standard scope items"
												icon={<FaClipboardList />}
												label="Organization Inclusions"
												labelLines={['Organization', 'Inclusions']}
												to="/organization-inclusions"
											/>
										)}
										<PrimaryNavLink
											active={path === '/vendor-management'}
											description="Directory and outreach"
											icon={<FaAddressBook />}
											label="Vendor Management"
											labelLines={['Vendor', 'Management']}
											to="/vendor-management"
										/>
										<PrimaryNavLink
											active={path === '/user'}
											description="Users and access"
											icon={<FaUsers />}
											label="Team"
											to="/user"
										/>
									</SidebarList>
								)}
							</Sidebar>
						)}
					<PageContainer>
						{projectFeatureBackLink}
						{children}
					</PageContainer>
				</InnerWrapper>
			)}

			{localStorage.getItem('USER_ROLE') === 'PO' && (
				<InnerWrapper>
					<Sidebar
						aria-hidden={expand || isProjectFeature}
						data-testid="global-sidebar"
						sidebarHide={expand || isProjectFeature}
					>
						{!isProjectFeature && (
							<SidebarList>
								<Link to="/dashboard">
									<SidebarListitem active={path === '/dashboard'}>Dashboard</SidebarListitem>
								</Link>
								<Link to="/subscriptionPlan">
									<SidebarListitem active={path === '/subscriptionPlan'}>
										Subscription Plans
									</SidebarListitem>
								</Link>
								<Link to="/enterprisePlan">
									<SidebarListitem active={path === '/enterprisePlan'}>
										Enterprise Plans
									</SidebarListitem>
								</Link>
								<Link to="/trade">
									<SidebarListitem
										active={path === '/trade' || path === '/trade-view/:tradeId'}
										style={{ display: 'flex', textAlign: 'center', fontWeight: 700 }}
									>
										Trades
									</SidebarListitem>
								</Link>
								<Link to="/scrubplan-network">
									<SidebarListitem active={path === '/scrubplan-network'}>
										ScrubPlan Network
									</SidebarListitem>
								</Link>
								<Link to="/exclusivity-review-settings">
									<SidebarListitem active={path === '/exclusivity-review-settings'}>
										Exclusivity Review
									</SidebarListitem>
								</Link>

								<Link to="/enterprise-billings">
									<SidebarListitem active={path === '/enterprise-billings'}>
										Enterprise Billings
									</SidebarListitem>
								</Link>
							</SidebarList>
						)}
					</Sidebar>
					<PageContainer>
						{projectFeatureBackLink}
						{children}
					</PageContainer>
				</InnerWrapper>
			)}
		</PageWrapper>
	);
}

export default memo(AppLayout);
